/**
 * Builders for the standard GA4 ecommerce item shape.
 *
 * Custom keys inside `ecommerce.items[]` do not make it into our GA4 Dataform
 * tables, so course attributes ride in the standard `item_category*` slots
 * instead.
 */

import { toIsoDateStr } from './dateUtils';

// Everything the EA/ULC cart can hold is a course offering, so this app never emits another category.
const ITEM_CATEGORY_COURSES = 'Courses';

const ON_DEMAND_LABEL = 'On-demand';

// "Fall B 2026" / "Spring 2027" — season, optional session letter, year.
const SESSION_NAME_PATTERN = /^(Spring|Summer|Fall|Winter)(?:\s+[A-Za-z])?\s+(\d{4})$/i;

const SELF_PACED_PATTERN = /^(on[-\s]?demand|self[-\s]?paced|continuous)$/i;

// Index by calendar month (1-12).
const SEASON_BY_MONTH = [
  null,
  'Spring',
  'Spring',
  'Spring',
  'Spring',
  'Summer',
  'Summer',
  'Summer',
  'Fall',
  'Fall',
  'Fall',
  'Fall',
  'Fall'
];

/**
 * Split a semicolon-separated interest track into trimmed, non-empty areas.
 * @param {string} interestTrack
 * @returns {string[]}
 */
export function splitInterestAreas(interestTrack) {
  return String(interestTrack || '')
    .split(';')
    .map((area) => area.trim())
    .filter((area) => area);
}

/**
 * Term label for `item_category5`, e.g. "Fall 2026".
 *
 * The offering's session name already carries season and year, so it is the
 * authoritative source and only its session letter is dropped. When no session
 * name is available the season is derived from the start month. Self-paced
 * offerings have no season and stay "On-demand".
 *
 * @param {object} params
 * @param {string} [params.sessionName] - e.g. "Fall B 2026"
 * @param {string|Date} [params.startDate] - fallback when sessionName is absent
 * @param {string} [params.courseType] - e.g. "Continuous"
 * @returns {string} e.g. "Fall 2026", "On-demand", or '' when undeterminable
 */
export function deriveTermLabel({ sessionName, startDate, courseType } = {}) {
  const session = String(sessionName || '').trim();

  if (SELF_PACED_PATTERN.test(session)) return ON_DEMAND_LABEL;

  // A self-paced course has no season even when it carries a stale seasonal
  // session name (e.g. a Continuous offering still pointing at "Summer C 2021").
  if (SELF_PACED_PATTERN.test(String(courseType || '').trim())) return ON_DEMAND_LABEL;

  const match = session.match(SESSION_NAME_PATTERN);
  if (match) {
    const season = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
    return `${season} ${match[2]}`;
  }

  const iso = toIsoDateStr(startDate);
  if (!iso) return '';

  const [year, month] = iso.split('-');
  const season = SEASON_BY_MONTH[Number(month)];

  return season ? `${season} ${year}` : '';
}

/**
 * Canvas course ids are positive integers; anything else (null, NaN, "NaN", decimals,
 * placeholder strings) must not masquerade as one.
 *
 * @param {string|number|null|undefined} value
 * @returns {string} digits, or '' when the value is not a valid Canvas course id
 */
export function normalizeCanvasCourseId(value) {
  if (typeof value === 'string') {
    const digits = value.trim();
    return /^\d+$/.test(digits) && Number(digits) > 0 ? String(Number(digits)) : '';
  }
  return Number.isInteger(value) && value > 0 ? String(value) : '';
}

/**
 * Pick the Canvas course id for a specific offering.
 *
 * When the offering object carries the `canvasCourseId` key, that value is authoritative —
 * even when null — so a selected term never inherits the course-level (primary offering)
 * shell. The course-level `lms.canvasCourseId` is used only when the offering object
 * predates the per-offering field.
 *
 * @param {object} [offering]
 * @param {object} [lms]
 * @returns {string|number|null}
 */
export function resolveOfferingCanvasCourseId(offering, lms) {
  if (offering && Object.prototype.hasOwnProperty.call(offering, 'canvasCourseId')) {
    return offering.canvasCourseId ?? null;
  }
  return lms?.canvasCourseId ?? null;
}

/**
 * Build one `ecommerce.items[]` entry in the standard GA4 shape.
 *
 * `price` is passed through as supplied so callers keep control of pricing.
 *
 * `item_id` is the Canvas course id — the key analytics joins to Salesforce / Aether
 * course connections. The RDS offering id rides along in `item_variant` for our own joins.
 *
 * @param {object} params
 * @param {string|number} [params.canvasCourseId] - Canvas course shell id
 * @param {string|number} [params.offeringId] - RDS hed__course_offering id
 * @param {string} [params.courseName] - bare course title, no prefix
 * @param {string} [params.coursePrefix] - e.g. "CEE 181"
 * @param {string} [params.interestTrack] - semicolon-separated interest areas
 * @param {string} [params.sessionName] - e.g. "Fall B 2026"
 * @param {string|Date} [params.startDate] - term fallback
 * @param {string} [params.courseType] - term fallback
 * @param {number} [params.price]
 * @param {number} [params.quantity]
 * @returns {object}
 */
export function buildGa4EcommerceItem({
  canvasCourseId,
  offeringId,
  courseName,
  coursePrefix,
  interestTrack,
  sessionName,
  startDate,
  courseType,
  price,
  quantity = 1
} = {}) {
  const [areaOfInterest1 = '', areaOfInterest2 = ''] = splitInterestAreas(interestTrack);

  return {
    item_id: normalizeCanvasCourseId(canvasCourseId),
    item_name: courseName || '',
    item_variant: String(offeringId || ''),
    item_category: ITEM_CATEGORY_COURSES,
    item_category2: areaOfInterest1,
    item_category3: areaOfInterest2,
    item_category4: coursePrefix || '',
    item_category5: deriveTermLabel({ sessionName, startDate, courseType }),
    price,
    quantity
  };
}
